import { useState, useEffect } from "react";
import { Boxes, RefreshCw, Trash2, Lock, LockOpen, X, Plus, Tag, Save } from "lucide-react";
import { api } from "@/lib/api";
import { useAzure } from "@/context/AzureContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

const REGIONS = ["eastus", "eastus2", "centralus", "westus2", "westus3", "northeurope", "westeurope", "uksouth", "southeastasia", "australiaeast"];

export default function ResourceGroups() {
  const { state, run, refresh } = useAzure();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const subs = state?.subscriptions || [];
  const rgs = state?.resource_groups || [];
  const [form, setForm] = useState({ name: "", subscription_id: "", location: "eastus" });
  const [tagRows, setTagRows] = useState([]);
  const [savingTags, setSavingTags] = useState(false);

  useEffect(() => {
    const t = selected?.tags || {};
    const rows = Object.entries(t).map(([key, value]) => ({ key, value }));
    setTagRows(rows.length ? rows : [{ key: "", value: "" }]);
  }, [selected]);

  const addTagRow = () => setTagRows((r) => [...r, { key: "", value: "" }]);
  const removeTagRow = (i) => setTagRows((r) => r.filter((_, idx) => idx !== i));
  const updateTagRow = (i, field, val) => setTagRows((r) => r.map((row, idx) => idx === i ? { ...row, [field]: val } : row));

  const saveTags = async () => {
    if (!selected) return;
    if (selected.lock?.type === "ReadOnly") return;
    const tags = {};
    tagRows.forEach(({ key, value }) => { const k = (key || "").trim(); if (k) tags[k] = value; });
    setSavingTags(true);
    const r = await run(() => api.post(`/azure/resource-groups/${selected.name}/tags`, { tags }), { success: "Tags applied" });
    setSavingTags(false);
    if (r.ok) {
      const fresh = await api.get("/azure/state");
      setSelected(fresh.data.resource_groups.find((x) => x.name === selected.name));
    }
  };

  const subName = (id) => subs.find((s) => s.id === id)?.name || id;

  const openCreate = () => { setForm({ name: "", subscription_id: subs[subs.length - 1]?.id || "", location: "eastus" }); setOpen(true); };

  const submit = () =>
    run(() => api.post("/azure/resource-groups", form), { success: `Created ${form.name}` }).then((r) => { if (r.ok) setOpen(false); });

  const del = (rg) => {
    const answer = window.prompt(`Type the resource group name to confirm deletion:\n${rg.name}`);
    if (answer !== rg.name) return;
    run(() => api.delete(`/azure/resource-groups/${rg.name}`), { success: "Resource group deleted" }).then((r) => { if (r.ok) setSelected(null); });
  };

  const setLock = (rg, lock_type) =>
    run(() => api.post(`/azure/resource-groups/${rg.name}/lock`, { lock_type, lock_name: lock_type ? `${lock_type}-lock` : "" }),
      { success: lock_type ? "Lock applied" : "Lock removed" }).then(async () => {
        const fresh = await api.get("/azure/state");
        setSelected(fresh.data.resource_groups.find((x) => x.name === rg.name));
      });

  return (
    <div data-testid="az-resource-groups">
      <div className="flex items-center gap-2 border-b border-border pb-3 mb-3">
        <Button size="sm" onClick={openCreate} data-testid="az-new-rg-btn" className="bg-[#0078d4] hover:bg-[#106ebe] text-white gap-2"><Boxes className="h-4 w-4" /> Create</Button>
        <Button size="sm" variant="ghost" onClick={refresh} className="gap-2"><RefreshCw className="h-4 w-4" /> Refresh</Button>
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Subscription</TableHead><TableHead>Location</TableHead><TableHead>Lock</TableHead></TableRow></TableHeader>
          <TableBody>
            {rgs.map((rg) => (
              <TableRow key={rg.id} className="cursor-pointer" data-testid={`az-rg-row-${rg.name}`} onClick={() => setSelected(rg)}>
                <TableCell className="font-medium text-[#0078d4]">{rg.name}</TableCell>
                <TableCell>{rg.subscription_name}</TableCell>
                <TableCell>{rg.location}</TableCell>
                <TableCell>{rg.lock ? <span className="inline-flex items-center gap-1 text-amber-600 text-xs"><Lock className="h-3 w-3" />{rg.lock.type}</span> : <span className="text-muted-foreground text-xs">—</span>}</TableCell>
              </TableRow>
            ))}
            {rgs.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No resource groups.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Create a resource group</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Subscription *</Label>
              <Select value={form.subscription_id} onValueChange={(v) => setForm({ ...form, subscription_id: v })}>
                <SelectTrigger data-testid="az-rg-sub"><SelectValue placeholder="Select subscription" /></SelectTrigger>
                <SelectContent>{subs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Resource group name *</Label><Input data-testid="az-rg-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="RG-FINANCE-PROD" /></div>
            <div className="space-y-1.5">
              <Label>Region *</Label>
              <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                <SelectTrigger data-testid="az-rg-region"><SelectValue /></SelectTrigger>
                <SelectContent>{REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="az-rg-create-submit" onClick={submit} className="bg-[#0078d4] hover:bg-[#106ebe] text-white" disabled={!form.subscription_id || !form.name}>Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {selected && (
        <div className="fixed inset-y-0 right-0 w-full max-w-md bg-card border-l border-border shadow-2xl z-50 flex flex-col" data-testid="az-rg-detail">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div><div className="font-display font-bold text-lg">{selected.name}</div><div className="text-xs text-muted-foreground">{selected.subscription_name} · {selected.location}</div></div>
            <Button size="icon" variant="ghost" onClick={() => setSelected(null)}><X className="h-4 w-4" /></Button>
          </div>
          <div className="px-5 py-3 border-b border-border flex gap-2 flex-wrap">
            {selected.lock ? (
              <Button size="sm" variant="outline" className="gap-2" data-testid="az-remove-lock-btn" onClick={() => setLock(selected, null)}><LockOpen className="h-4 w-4" /> Remove lock</Button>
            ) : (
              <>
                <Button size="sm" variant="outline" className="gap-2" data-testid="az-add-delete-lock-btn" onClick={() => setLock(selected, "CanNotDelete")}><Lock className="h-4 w-4" /> Delete lock</Button>
                <Button size="sm" variant="outline" className="gap-2" onClick={() => setLock(selected, "ReadOnly")}><Lock className="h-4 w-4" /> Read-only lock</Button>
              </>
            )}
            <Button size="sm" variant="outline" className="gap-2 text-red-600" data-testid="az-delete-rg-btn" onClick={() => del(selected)}><Trash2 className="h-4 w-4" /> Delete</Button>
          </div>
          <div className="p-5 space-y-3 text-sm overflow-y-auto">
            <div className="break-all"><span className="text-muted-foreground">Resource ID</span><div className="font-mono text-xs mt-1">{selected.id}</div></div>
            <div><span className="text-muted-foreground">Lock</span><span className="font-medium ml-2">{selected.lock ? `${selected.lock.type} (${selected.lock.name})` : "None"}</span></div>
            <div data-testid="az-rg-tags-editor">
              <div className="flex items-center justify-between mb-2">
                <span className="text-muted-foreground inline-flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" /> Tags</span>
                <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={addTagRow} data-testid="az-rg-tag-add" disabled={selected.lock?.type === "ReadOnly"}><Plus className="h-3.5 w-3.5" /> Add tag</Button>
              </div>
              {selected.lock?.type === "ReadOnly" && (
                <p className="text-xs text-amber-600 mb-2">This resource group has a Read-only lock. Remove the lock to edit tags.</p>
              )}
              <div className="space-y-2">
                {tagRows.map((row, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`az-rg-tag-row-${i}`}>
                    <Input placeholder="Name" value={row.key} onChange={(e) => updateTagRow(i, "key", e.target.value)} className="h-8 text-xs" data-testid={`az-rg-tag-key-${i}`} disabled={selected.lock?.type === "ReadOnly"} />
                    <span className="text-muted-foreground text-xs">=</span>
                    <Input placeholder="Value" value={row.value} onChange={(e) => updateTagRow(i, "value", e.target.value)} className="h-8 text-xs" data-testid={`az-rg-tag-value-${i}`} disabled={selected.lock?.type === "ReadOnly"} />
                    <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => removeTagRow(i)} data-testid={`az-rg-tag-remove-${i}`} disabled={selected.lock?.type === "ReadOnly"}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
              <Button size="sm" className="mt-3 gap-2 bg-[#0078d4] hover:bg-[#106ebe] text-white" onClick={saveTags} data-testid="az-rg-tag-save" disabled={savingTags || selected.lock?.type === "ReadOnly"}>
                <Save className="h-4 w-4" /> {savingTags ? "Saving…" : "Apply tags"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
